import { useId } from "react";
import { cn } from "@/lib/utils";
import type { VaultFile } from "../../types";
import { formatDate } from "../../lib/format-date";

export function VaultFileRow({
  file,
  selected,
  selectionMode,
  downloadsBusy,
  onSelect,
  onPreview,
}: {
  file: VaultFile;
  selected: boolean;
  selectionMode: boolean;
  downloadsBusy: boolean;
  onSelect: (selected: boolean) => void;
  onPreview: () => void;
}) {
  const descriptionId = useId();
  const primaryName = file.typeLabel?.trim() || file.fileName;
  const showFileName = primaryName !== file.fileName;
  const unavailableReason =
    file.unavailableReason ?? "This file is not available yet.";
  const descriptions = [
    file.expiresAt ? `${descriptionId}-expiry` : null,
    !file.canDownload ? `${descriptionId}-unavailable` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <li className={cn("flex items-center", selected && "bg-primary/5")}>
      <input
        type="checkbox"
        aria-label={`Select ${file.fileName}`}
        checked={selected}
        disabled={!file.canDownload || downloadsBusy}
        title={file.unavailableReason ?? undefined}
        className={cn(
          "ml-3 size-4 shrink-0 accent-primary sm:ml-4",
          !selectionMode && "hidden sm:block",
        )}
        onChange={(event) => onSelect(event.target.checked)}
      />
      <button
        type="button"
        className="flex min-h-16 min-w-0 flex-1 cursor-pointer flex-col items-stretch justify-between gap-2 px-3 py-3 text-left transition-colors hover:bg-primary/5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary disabled:cursor-default disabled:text-slate-500 disabled:hover:bg-transparent min-[360px]:flex-row min-[360px]:items-center min-[360px]:gap-3 sm:px-4"
        aria-label={`Preview ${primaryName}${showFileName ? `: ${file.fileName}` : ""}`}
        aria-describedby={descriptions || undefined}
        title={file.canDownload ? file.fileName : unavailableReason}
        disabled={!file.canDownload}
        onClick={onPreview}
      >
        <span className="min-w-0 flex-1 space-y-0.5">
          <span
            className={cn(
              "block truncate text-sm font-semibold text-slate-900",
              !file.canDownload && "text-slate-500",
            )}
          >
            {primaryName}
          </span>
          {showFileName && (
            <span className="block truncate text-xs text-slate-500">
              {file.fileName}
            </span>
          )}
        </span>
        {file.expiresAt && (
          <span
            id={`${descriptionId}-expiry`}
            className="flex shrink-0 gap-1.5 text-xs leading-5 text-slate-600 min-[360px]:block min-[360px]:text-right"
          >
            <span className="block">Expiration</span>
            <time
              dateTime={file.expiresAt}
              className="block whitespace-nowrap tabular-nums"
            >
              {formatDate(file.expiresAt)}
            </time>
          </span>
        )}
      </button>
      {!file.canDownload && (
        <span id={`${descriptionId}-unavailable`} className="sr-only">
          {unavailableReason}
        </span>
      )}
    </li>
  );
}
